import { logoutUser } from '../../../../../../inplant-core-fe/redux/actions'
import { EngineTorqueDensifierProps, EngineTorqueDensifierState } from './_types'
import { ChartFetch } from '../../ChartRenderUtilities'
import { parseDuration } from '@mv-submodules/inplant-plantanalysis-fe-iblu/functions/sharedV2'
import { engineTorqueDensifierMeasuresToFetch } from './_measures'
import { getInfluxSummaryMeasuresData } from '@mv-submodules/inplant-plantanalysis-fe-iblu/functions/influx/getChartInfluxData'

interface DataGroupedByDec {
  [bufferKey: string]: {
    [metricKey: string]: any
  }
}
abstract class TorqueBelt<
  Props extends EngineTorqueDensifierProps,
  State extends EngineTorqueDensifierState
> extends ChartFetch<Props, State> {
  // private mounted = false
  private abortController: AbortController = new AbortController()
  protected chartTranslationKeyTitle: string = 'plantAnalysis.lines.engineTorqueDensifierTitle'

  constructor(props: Props, generateState: (props: Props) => State) {
    super(props)
    this.state = generateState(props)
    this.getData = this.getData.bind(this)
  }

  public componentDidMount() {
    this.getData()
  }

  public componentWillUnmount() {
    this.abortController.abort()
  }

  protected getSecondsPercentFromTotalWorkshift(lineSecondsAtZero: number): number | undefined {
    const workShiftTotalSeconds = this.getWorkshiftTotalSeconds()
    if (workShiftTotalSeconds) {
      return (lineSecondsAtZero / workShiftTotalSeconds) * 100
    }
    return undefined
  }

  protected getWorkshiftTotalSeconds() {
    const { workshift } = this.props
    if (workshift) {
      if (workshift.value === 99) {
        return parseDuration('24h 00m').asSeconds()
      } else {
        return parseDuration(workshift.h).asSeconds()
      }
    }
    return undefined
  }

  public componentDidUpdate(prevProps: Readonly<EngineTorqueDensifierProps>) {
    if (
      this.props.dateFilterStart !== prevProps.dateFilterStart ||
      this.props.dateFilterEnd !== prevProps.dateFilterEnd ||
      this.props.workShift?.value !== prevProps.workShift?.value ||
      (this.props.plant.plant !== prevProps.plant.plant && !prevProps.plant.isLoading)
    ) {
      if (this.props.dateFilterStart && this.props.dateFilterEnd) {
        this.getData()
      }
    }
  }

  /**
   * Groups data by DEC (densifier) identifier, organizing metrics by their buffer keys.
   *
   * @param data - Record containing key-value pairs where keys follow the pattern "dec_f{number}_{metric}"
   * @returns Grouped data organized by buffer keys (e.g., "dec_f019", "dec_f028") with their respective metrics
   *
   * @example
   * Input: { "dec_f019_torque": 100, "dec_f019_speed": 50, "dec_f028_torque": 80 }
   * Output: {
   *   "dec_f019": { "torque": 100, "speed": 50 },
   *   "dec_f028": { "torque": 80 }
   * }
   */
  private groupDataByDec(data: Record<string, number>) {
    return Object.entries(data).reduce<DataGroupedByDec>((acc, [key, value]) => {
      const match = key.match(/(dec_f\d+)_([\w_]+)/)
      if (match) {
        const [, bufferKey, metricKey] = match
        if (!acc[bufferKey]) {
          acc[bufferKey] = {}
        }
        acc[bufferKey][metricKey] = value
      }
      return acc
    }, {})
  }

  private async getData() {
    const workshift = this.props.workshift

    if (workshift) {
      try {
        this.setState({
          fetching: true,
          error: false,
        })

        const measuresData = await getInfluxSummaryMeasuresData(
          {
            measuresToFetch: engineTorqueDensifierMeasuresToFetch,
            plant: this.props.plant.plant,
            date: this.props.date.toString(),
            workshift,
          },
          {
            abortController: this.abortController,
          }
        )

        const dataGrouped = this.groupDataByDec({
          ...measuresData.decSpeedTorque,
          ...measuresData.decUptime,
          ...measuresData.decMeanScrewSpeed,
        })

        this.setState({
          fetching: false,
          data: dataGrouped,
        })
      } catch (error: any) {
        if (error.name === 'FetchError' && error.statusCode === 401) {
          logoutUser()
        }
        this.setState({
          fetching: false,
          error: true,
        })
      }
    }
  }

  protected abstract renderContent(): JSX.Element
  public render() {
    const { fetching,error } = this.state

    if (fetching) {
      return this.renderLoading()
    }

    if (error) {
      return this.renderError()
    }

    if (!this.state.data) {
      return this.renderNoData()
    }

    return this.renderContent()
  }
}

export default TorqueBelt
